import React, { useState, useEffect } from 'react';
import '../estilos.css';
import config from '../config.json';
import { generarUUID } from '../Component/generadorUUID';

const ITEMS_PER_PAGE = 5;

const TablesCreated: React.FC<{
  sessionUUID?: string | null;
  refresh?: boolean;
  onTablesLoaded?: (count: number, tables: string[]) => void;
  onEditTable?: (tableName: string) => void;
}> = ({ sessionUUID, refresh, onTablesLoaded, onEditTable }) => {

  const [isLoading, setIsLoading] = useState(false);
  const [foreignTables, setForeignTables] = useState<string[]>([]);
  const [localUUID, setLocalUUID] = useState<string | null>(localStorage.getItem('sessionUUID'));
  const [tableToDelete, setTableToDelete] = useState<string | null>(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!localUUID) {
      const newUUID = generarUUID();
      localStorage.setItem('sessionUUID', newUUID);
      setLocalUUID(newUUID);
    }
  }, [localUUID]);

  const fetchTables = () => {
    setIsLoading(true);
    const requestData = { action: 'listTables', databaseName: sessionUUID || localUUID };
    fetch(`${config.ENDPOINTS.CREAR_TABLAS}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestData),
    })
      .then(async (response) => {
        const text = await response.text();
        try { return JSON.parse(text); }
        catch { throw new Error('La respuesta no es JSON válido'); }
      })
      .then((data) => {
        const tables = data.tables || [];
        setForeignTables(tables);
        setIsLoading(false);
        setCurrentPage(1);
        if (onTablesLoaded) onTablesLoaded(tables.length, tables);
      })
      .catch((error) => {
        console.error('Error al obtener tablas:', error);
        setIsLoading(false);
      });
  };

  useEffect(() => { fetchTables(); }, [refresh]);

  const handleConfirmDelete = async () => {
    if (!tableToDelete) return;

    const requestData = {
        action: 'deleteTable',
        databaseName: sessionUUID || localUUID,
        tableName: tableToDelete,
    };
    await fetch(`${config.ENDPOINTS.CREAR_TABLAS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
    }).then(async (response) => {
        const text = await response.text();
        try { return JSON.parse(text); }
        catch { throw new Error('La respuesta no es JSON válido'); }
    });

    const projectConfig = JSON.parse(localStorage.getItem('projectConfig') || '{}');
    if (projectConfig?.framework && projectConfig?.sessionUUID) {
        try {
            await fetch(`${config.ENDPOINTS.CALLER_LANGUAGE}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'removeModule',
                    tableToRemove: tableToDelete,
                    framework: projectConfig.framework,
                    databaseName: projectConfig.sessionUUID,
                }),
            });
        } catch (e) {
            console.error('Error al quitar módulo del sidebar:', e);
        }
    }

    setTableToDelete(null);
    fetchTables();
  };

  const handleConfirmDeleteAll = async () => {
    setIsDeletingAll(true);
    try {
      await Promise.all(
        foreignTables.map((tableName) =>
          fetch(`${config.ENDPOINTS.CREAR_TABLAS}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'deleteTable',
              databaseName: sessionUUID || localUUID,
              tableName,
            }),
          })
        )
      );
    } catch (error) {
      console.error('Error al eliminar todas las tablas:', error);
    } finally {
      setIsDeletingAll(false);
      setShowDeleteAllModal(false);
      fetchTables();
    }
  };

  const totalPages = Math.ceil(foreignTables.length / ITEMS_PER_PAGE);
  const paginated = foreignTables.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <div className='tableslist'>
      <style>{`
        @keyframes popIn {
          from { opacity: 0; transform: scale(0.88); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .table-row-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 18px;
          margin: 5px 10px;
          border-radius: 10px;
          background: #fff;
          border: 1px solid #e9ecef;
          transition: all 0.18s ease;
          animation: fadeInUp 0.2s ease both;
        }
        .table-row-item:hover {
          background: #f0f7ff;
          border-color: #b8d4f5;
          box-shadow: 0 2px 8px rgba(13,110,253,0.08);
          transform: translateY(-1px);
        }
        .table-row-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px; height: 32px;
          border-radius: 8px;
          background: linear-gradient(135deg, #e8f0fe, #d0e4ff);
          font-size: 15px;
          flex-shrink: 0;
        }
        .table-row-name {
          font-weight: 600;
          font-size: 14px;
          color: #212529;
          letter-spacing: 0.01em;
        }
        .table-rows-wrapper {
          padding: 8px 0 10px;
        }
        .pagination-wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 18px 14px;
          font-size: 13px;
          color: #888;
        }
        .pagination-buttons {
          display: flex;
          gap: 4px;
        }
        .page-btn {
          width: 32px; height: 32px;
          border-radius: 7px;
          border: 1px solid #e0e0e0;
          background: #fff;
          color: #444;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
          display: flex; align-items: center; justify-content: center;
        }
        .page-btn:hover:not(:disabled) { background: #f0f7ff; border-color: #b8d4f5; }
        .page-btn.active { background: #1a7fd4; color: #fff; border-color: #1a7fd4; }
        .page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .modal-btn {
          padding: 11px 28px;
          font-size: 14px;
          font-weight: 600;
          border-radius: 10px;
          border: none;
          cursor: pointer;
          min-width: 120px;
          transition: all 0.15s ease;
        }
        .modal-btn-cancel {
          background: #f5f5f5;
          color: #444;
          border: 1px solid #ddd !important;
        }
        .modal-btn-cancel:hover { background: #eaeaea; }
        .modal-btn-danger {
          background: linear-gradient(135deg, #e53935, #c62828);
          color: #fff;
          box-shadow: 0 4px 12px rgba(220,53,69,0.35);
        }
        .modal-btn-danger:hover {
          background: linear-gradient(135deg, #c62828, #b71c1c);
          box-shadow: 0 6px 16px rgba(220,53,69,0.45);
          transform: translateY(-1px);
        }
        .modal-btn-danger:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        .btn-delete-all {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 14px;
          font-size: 13px;
          font-weight: 600;
          border-radius: 8px;
          border: 1px solid #f5c6cb;
          background: #fff5f5;
          color: #c62828;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .btn-delete-all:hover {
          background: #fdecea;
          border-color: #e53935;
          box-shadow: 0 2px 8px rgba(220,53,69,0.15);
        }
        .btn-edit-table {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 11px;
          font-size: 13px;
          font-weight: 600;
          border-radius: 8px;
          border: 1px solid #b8d4f5;
          background: #e8f0fe;
          color: #1a5dab;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .btn-edit-table:hover {
          background: #d0e4ff;
          border-color: #1a7fd4;
          box-shadow: 0 2px 8px rgba(26,127,212,0.15);
        }
      `}</style>

      <hr />
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3 className="card-title mb-0">Tablas creadas</h3>
            {foreignTables.length > 0 && (
              <span className="badge bg-secondary">{foreignTables.length}</span>
            )}
          </div>

          {foreignTables.length > 0 && (
            <button
              className="btn-delete-all"
              onClick={() => setShowDeleteAllModal(true)}
            >
              <i className="fas fa-trash-alt" />
              Eliminar todo
            </button>
          )}
        </div>

        {isLoading && <div className="spinner p-3">Cargando...</div>}

        {!isLoading && foreignTables.length === 0 && (
          <div style={{ padding: '28px', textAlign: 'center', color: '#aaa', fontSize: '14px' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🗃️</div>
            No hay tablas creadas aún.
          </div>
        )}

        {!isLoading && foreignTables.length > 0 && (
          <>
            <div className="table-rows-wrapper">
              {paginated.map((row, index) => (
                <div key={row} className="table-row-item" style={{ animationDelay: `${index * 0.04}s` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span className="table-row-icon">🗄️</span>
                    <span className="table-row-name">{row}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {onEditTable && (
                      <button
                        className="btn-edit-table"
                        onClick={() => onEditTable(row)}
                        title="Editar tabla"
                      >
                        ✏️ Editar
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-danger"
                      style={{ borderRadius: '8px', padding: '4px 12px', fontSize: '13px' }}
                      onClick={() => setTableToDelete(row)}
                    >
                      <i className="fas fa-times me-1"></i>Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination-wrapper">
                <span>
                  Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, foreignTables.length)} de {foreignTables.length}
                </span>
                <div className="pagination-buttons">
                  <button className="page-btn" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>‹</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button key={page} className={`page-btn ${page === currentPage ? 'active' : ''}`} onClick={() => setCurrentPage(page)}>
                      {page}
                    </button>
                  ))}
                  <button className="page-btn" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>›</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* MODAL — Eliminar una tabla */}
      {tableToDelete && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px',
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px',
            padding: '36px',
            maxWidth: '400px', width: '100%', minHeight: '257px',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            textAlign: 'center',
            animation: 'popIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗑️</div>
            <h5 style={{ fontSize: '20px', fontWeight: 700, color: '#1a1a1a', marginBottom: '10px' }}>
              ¿Eliminar tabla?
            </h5>
            <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.6, marginBottom: '8px' }}>
              Estás a punto de eliminar <strong style={{ color: '#1a1a1a' }}>{tableToDelete}</strong>.
            </p>
            <span style={{ display: 'block', fontSize: '13px', color: '#dc3545', marginBottom: '20px' }}>
              Esta acción no se puede deshacer.
            </span>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button className="modal-btn modal-btn-cancel" onClick={() => setTableToDelete(null)}>Cancelar</button>
              <button className="modal-btn modal-btn-danger" onClick={handleConfirmDelete}>Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL — Eliminar todas las tablas */}
      {showDeleteAllModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px',
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px',
            padding: '36px',
            maxWidth: '420px', width: '100%',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            textAlign: 'center',
            animation: 'popIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h5 style={{ fontSize: '20px', fontWeight: 700, color: '#1a1a1a', marginBottom: '10px' }}>
              ¿Eliminar todas las tablas?
            </h5>
            <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.6, marginBottom: '8px' }}>
              Estás a punto de eliminar las{' '}
              <strong style={{ color: '#1a1a1a' }}>{foreignTables.length} tabla{foreignTables.length !== 1 ? 's' : ''}</strong>{' '}
              de tu base de datos.
            </p>
            <span style={{ display: 'block', fontSize: '13px', color: '#dc3545', marginBottom: '20px' }}>
              Esta acción es permanente y no se puede deshacer.
            </span>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                className="modal-btn modal-btn-cancel"
                onClick={() => setShowDeleteAllModal(false)}
                disabled={isDeletingAll}
              >
                Cancelar
              </button>
              <button
                className="modal-btn modal-btn-danger"
                onClick={handleConfirmDeleteAll}
                disabled={isDeletingAll}
              >
                {isDeletingAll ? 'Eliminando...' : 'Sí, eliminar todo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TablesCreated;